// src/components/Dashboard/FilesView.tsx
/**
 * Files View — shows all files from a dashboard/project with file-type icons.
 * PDF and video files can be previewed inline; others trigger download.
 * Based on GalleryView pattern but uses FileCard instead of AssetCard.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { dashboardApi } from '@/services/dashboardApi';
import { authStorage } from '@/lib/storage';
import { downloadAttachment } from '@/services/filesService';
import { FileCard } from '@/components/FileCard/FileCard';
import VideoModal from '@/components/Globals/VideoModal';
import PdfModal from '@/components/Globals/PdfModal';
import type { PersonFile } from '@/services/homeService';
import type { Attachment } from '@/types/conversation';
import { config } from '@config/env';

const PAGE_SIZE = 100;

const VIDEO_EXTENSIONS = ['.3gp', '.mp4', '.mov', '.ogg', '.webm', '.f4v', '.m4v', '.mkv'];
const PDF_EXTENSIONS = ['.pdf'];

export interface FilesViewProps {
  dashboardId: number;
  projectId: string;
  filters: string;
  searchText: string;
  extraFilterParams?: Record<string, any>;
  onOpenInfoWindow: (conversationId: number) => void;
}

/** Build an ApiConfig object for PdfModal / VideoModal */
function getApiConfig()
{
  return {
    url: config.api.baseUrl,
    token: authStorage.getAccessToken() || '',
    refreshToken: '',
    TenancyName: config.tenant.defaultName,
  };
}

/** Convert a PersonFile to the Attachment shape expected by modals */
function toAttachment(file: PersonFile): Attachment
{
  return {
    AttachmentId: file.AttachmentId,
    FileName: file.FileName,
    FileUrl: file.FileUrl,
    FileExtension: file.FileExtension,
    FileType: file.FileContentType || '',
    Description: file.Description,
    FileSize: file.FileSize,
  } as Attachment;
}

export const FilesView: React.FC<FilesViewProps> = ({
  dashboardId,
  projectId,
  filters,
  searchText,
  extraFilterParams,
  onOpenInfoWindow,
}) =>
{
  const [files, setFiles] = useState<PersonFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noFiles, setNoFiles] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Video modal state
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoAttachment, setVideoAttachment] = useState<Attachment | null>(null);

  // PDF modal state
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfAttachment, setPdfAttachment] = useState<Attachment | null>(null);

  // Load data when dashboard/filters/search change
  useEffect(() =>
  {
    let cancelled = false;

    const load = async () =>
    {
      try
      {
        setLoading(true);
        setNoFiles(false);

        const response = await dashboardApi.getGalleryFileProject({
          CustomFields: [],
          Option: 2,
          PageNumber: 1,
          PageSize: PAGE_SIZE,
          DashboardId: dashboardId,
          Filters: filters,
          SearchText: searchText,
          LayersToHide: '',
          typeFilter: 0,
          Project: projectId,
          ...extraFilterParams,
        });

        if (cancelled) return;

        if (response.length === 0)
        {
          setNoFiles(true);
        }

        setFiles(response);
        setHasMore(response.length >= PAGE_SIZE);
        setPageNumber(1);
      }
      catch (error)
      {
        console.error('Error loading files:', error);
        if (!cancelled) setNoFiles(true);
      }
      finally
      {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () =>
    {
      cancelled = true;
    };
  }, [dashboardId, filters, searchText, projectId, extraFilterParams]);

  // Load more (infinite scroll)
  const loadMore = useCallback(async () =>
  {
    if (!hasMore || loadingMore) return;

    try
    {
      setLoadingMore(true);
      const nextPage = pageNumber + 1;

      const response = await dashboardApi.getGalleryFileProject({
        CustomFields: [],
        Option: 2,
        PageNumber: nextPage,
        PageSize: PAGE_SIZE,
        DashboardId: dashboardId,
        Filters: filters,
        SearchText: searchText,
        LayersToHide: '',
        typeFilter: 0,
        Project: projectId,
        ...extraFilterParams,
      });

      if (response.length > 0)
      {
        setFiles(prev => [...prev, ...response]);
        setPageNumber(nextPage);
        setHasMore(response.length >= PAGE_SIZE);
      }
      else
      {
        setHasMore(false);
      }
    }
    catch (error)
    {
      console.error('Error loading more files:', error);
    }
    finally
    {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    loadingMore,
    pageNumber,
    dashboardId,
    filters,
    searchText,
    projectId,
    extraFilterParams,
  ]);

  // Scroll handler
  const handleScroll = useCallback(() =>
  {
    const container = scrollContainerRef.current;
    if (!container || loadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop - clientHeight < 800)
    {
      loadMore();
    }
  }, [loadMore, loadingMore, hasMore]);

  // Handle file click: PDF → PdfModal, Video → VideoModal, other → download
  const handleFileClick = useCallback((file: PersonFile) =>
  {
    const ext = (file.FileExtension || '').toLowerCase();

    if (VIDEO_EXTENSIONS.includes(ext))
    {
      setVideoAttachment(toAttachment(file));
      setVideoUrl(file.FileUrl);
      setVideoOpen(true);
    }
    else if (PDF_EXTENSIONS.includes(ext))
    {
      setPdfAttachment(toAttachment(file));
      setPdfOpen(true);
    }
    else
    {
      // Download — open in new tab
      if (file.FileUrl)
      {
        window.open(file.FileUrl, '_blank');
      }
    }
  }, []);

  const handleTitleClick = useCallback(
    (conversationId: number) =>
    {
      onOpenInfoWindow(conversationId);
    },
    [onOpenInfoWindow],
  );

  const handleCloseVideo = useCallback(() =>
  {
    setVideoOpen(false);
    setVideoUrl('');
    setVideoAttachment(null);
  }, []);

  const handleClosePdf = useCallback(() =>
  {
    setPdfOpen(false);
    setPdfAttachment(null);
  }, []);

  const api = getApiConfig();
  const isDark = document.documentElement.classList.contains('dark');

  // Loading skeleton — matches FileCard structure
  if (loading && files.length === 0)
  {
    return (
      <div
        className="h-full overflow-hidden p-2"
        style={{ backgroundColor: isDark ? '#2d2f2f' : '#ededed' }}
      >
        <div className="project-files-items">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => (
            <div key={i} className="project-files-item animate-pulse">
              {/* Icon area */}
              <div className="project-files-item-details">
                <div className="project-files-item-image">
                  <div
                    className="project-files-item-thumbnail"
                    style={{
                      backgroundColor: '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        backgroundColor: isDark ? '#4a4c4c' : '#d1d5db',
                        borderRadius: 4,
                      }}
                    />
                  </div>
                </div>
                {/* Description + author */}
                <div className="project-files-details">
                  <div
                    style={{
                      height: 12,
                      backgroundColor: isDark ? '#4a4c4c' : '#d1d5db',
                      borderRadius: 2,
                      width: '80%',
                      marginBottom: 6,
                    }}
                  />
                  <div
                    style={{
                      height: 9,
                      backgroundColor: isDark ? '#3b3d3d' : '#e5e7eb',
                      borderRadius: 2,
                      width: '50%',
                      marginBottom: 4,
                    }}
                  />
                  <div
                    style={{
                      height: 9,
                      backgroundColor: isDark ? '#3b3d3d' : '#e5e7eb',
                      borderRadius: 2,
                      width: '65%',
                    }}
                  />
                </div>
              </div>
              {/* Bottom row */}
              <div className="project-files-item-card">
                <div className="project-files-item-card-layers">
                  <span
                    className="layer-tag"
                    style={{ backgroundColor: isDark ? '#4a4c4c' : '#d1d5db' }}
                  />
                </div>
                <div className="project-files-item-card-icon">
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      backgroundColor: isDark ? '#4a4c4c' : '#d1d5db',
                      borderRadius: 2,
                    }}
                  />
                </div>
                <div className="project-files-item-card-name">
                  <div
                    style={{
                      height: 10,
                      backgroundColor: isDark ? '#4a4c4c' : '#d1d5db',
                      borderRadius: 2,
                      width: '70%',
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (noFiles && files.length === 0)
  {
    return (
      <div className="assets-tab-content h-full flex items-center justify-center">
        <div className="text-center py-16">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No files to display</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollContainerRef}
      className="user-attachments user-attachments-tab-view h-full overflow-auto"
      style={{ backgroundColor: '#ededed' }}
      onScroll={handleScroll}
    >
      <div className="project-files-items" style={{ paddingBottom: 30 }}>
        {files.map((file, index) => (
          <FileCard
            key={`${file.AttachmentId}-${index}`}
            file={file}
            onClickFile={handleFileClick}
            onClickTitle={handleTitleClick}
          />
        ))}
      </div>

      {/* No more items */}
      {!hasMore && files.length > 0 && (
        <div className="text-center py-4">
          <span className="text-gray-400 text-sm">No more files.</span>
        </div>
      )}

      {/* Loading more indicator */}
      {loadingMore && (
        <div className="text-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5dade2] mx-auto" />
        </div>
      )}

      {/* Video Modal */}
      {videoAttachment && (
        <VideoModal
          openModal={videoOpen}
          url={videoUrl}
          playing={videoOpen}
          controls
          attachments={videoAttachment}
          onFileDownloadVideo={(_e, att) =>
          {
            downloadAttachment(
              att.AttachmentId,
              null,
              (att.Description || att.FileName) + (att.FileExtension || ''),
              att.FileUrl || '',
              { apiUrl: api.url, token: authStorage.getAccessToken() || '' },
            );
          }}
          api={api}
          onClose={handleCloseVideo}
          hideActionButtons
        />
      )}

      {/* PDF Modal */}
      {pdfAttachment && (
        <PdfModal
          openModal={pdfOpen}
          attachment={pdfAttachment}
          onFileDownload={(_e, att) =>
          {
            downloadAttachment(
              att.AttachmentId,
              null,
              (att.Description || att.Description) + (att.FileExtension || ''),
              att.FileUrl || '',
              { apiUrl: api.url, token: authStorage.getAccessToken() || '' },
            );
          }}
          api={api}
          onClose={handleClosePdf}
          hideActionButtons
        />
      )}
    </div>
  );
};

export default FilesView;
